import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import './TemplateCard.css';

/**
 * Displays a diet template as a card, showing title, language, intolerances,
 * illnesses, and a preview of the foods contained in the diet plan.
 *
 * @param {Object}   props.template             - The template object from the server.
 * @param {Function} props.onClick              - Callback invoked when the card is clicked.
 * @param {boolean}  [props.showActionRow=false] - When true, shows edit/delete row at the bottom.
 * @param {Function} [props.onEdit]             - Callback invoked when the edit icon is clicked.
 * @param {Function} [props.onDelete]           - Callback invoked when the delete icon is clicked.
 */
const TemplateCard = ({ template, onClick, showActionRow = false, onEdit, onDelete }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

    // Compute average daily macros across all days that have at least one food
    const dietPlan = template.dietPlan ?? {};
    let totalKcal = 0, totalProteins = 0, totalCarbs = 0, totalFats = 0;
    let activeDays = 0;
    Object.values(dietPlan).forEach(day => {
        if (typeof day !== 'object' || !day) return;
        let dayKcal = 0, dayProteins = 0, dayCarbs = 0, dayFats = 0, dayHasFood = false;
        Object.values(day).forEach(meal => {
            if (!Array.isArray(meal?.foods)) return;
            meal.foods.forEach(food => {
                dayKcal     += parseFloat(food.kcal     ?? 0);
                dayProteins += parseFloat(food.proteins ?? 0);
                dayCarbs    += parseFloat(food.carbs    ?? 0);
                dayFats     += parseFloat(food.fats     ?? 0);
                dayHasFood = true;
            });
        });
        if (dayHasFood) {
            totalKcal     += dayKcal;
            totalProteins += dayProteins;
            totalCarbs    += dayCarbs;
            totalFats     += dayFats;
            activeDays++;
        }
    });
    const hasData = activeDays > 0;
    const avgKcal     = hasData ? Math.round(totalKcal     / activeDays) : 0;
    const avgProteins = hasData ? (totalProteins / activeDays).toFixed(1) : '0';
    const avgCarbs    = hasData ? (totalCarbs    / activeDays).toFixed(1) : '0';
    const avgFats     = hasData ? (totalFats     / activeDays).toFixed(1) : '0';

    const intolerances = template.intolerances ?? [];
    const illnesses    = template.illnesses    ?? [];

    const resolveLocalizedName = (item) => {
        if (typeof item !== 'object') return item;
        const n = item.name;
        if (!n) return '';
        if (typeof n === 'object') return n[lang] || n.en || n.it || '';
        return n;
    };

    return (
        <div className="template-card" onClick={onClick}>
            <div className="template-card-header">
                <span className="template-card-title">{template.title}</span>
                {template.language && (
                    <span className="template-card-lang">
                        {template.language.toUpperCase()}
                    </span>
                )}
            </div>

            <div className="template-card-intolerances">
                {intolerances.length > 0 ? (
                    <div className="template-card-intol-tags">
                        {intolerances.map((intol, idx) => (
                            <span key={idx} className="template-intol-tag">
                                {resolveLocalizedName(intol)}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="template-card-no-intol">{t('no-intolerances')}</span>
                )}
            </div>

            <div className="template-card-illnesses">
                {illnesses.length > 0 ? (
                    <div className="template-card-illness-tags">
                        {illnesses.map((illness, idx) => (
                            <span key={idx} className="template-illness-tag">
                                {resolveLocalizedName(illness)}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="template-card-no-illness">{t('no-illnesses')}</span>
                )}
            </div>

            <div className="template-card-macros">
                {!hasData ? (
                    <span className="template-card-no-foods">{t('no-foods-preview')}</span>
                ) : (
                    <>
                        <div className="template-macro-kcal">
                            <span className="template-macro-kcal-value">{avgKcal}</span>
                            <span className="template-macro-kcal-unit">{t('kcal')}</span>
                            <span className="template-macro-kcal-label">/{t('day')}</span>
                        </div>
                        <div className="template-macro-row">
                            <div className="template-macro-item template-macro-proteins">
                                <span className="template-macro-value">{avgProteins}g</span>
                                <span className="template-macro-label">{t('Proteins')}</span>
                            </div>
                            <div className="template-macro-item template-macro-carbs">
                                <span className="template-macro-value">{avgCarbs}g</span>
                                <span className="template-macro-label">{t('Carbs')}</span>
                            </div>
                            <div className="template-macro-item template-macro-fats">
                                <span className="template-macro-value">{avgFats}g</span>
                                <span className="template-macro-label">{t('Fats')}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {showActionRow && (
                <div className="template-card-actions-row" onClick={(e) => e.stopPropagation()}>
                    <button
                        className="template-card-action-btn template-card-action-btn--edit"
                        title={t('edit-template')}
                        aria-label={t('edit-template')}
                        onClick={onEdit}
                    >
                        <FaEdit />
                    </button>
                    <button
                        className="template-card-action-btn template-card-action-btn--delete"
                        title={t('delete-template')}
                        aria-label={t('delete-template')}
                        onClick={onDelete}
                    >
                        <FaTrash />
                    </button>
                </div>
            )}
        </div>
    );
};

export default TemplateCard;
